import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import type { Tool } from "@shared/types";
import { get } from "@/lib/api";

/**
 * "Utensils" — the dropdown of other tools and apps.
 *
 * Kept separate from the main nav so the projects are reachable without
 * every one of them taking a slot in the header. Contents come from the
 * `tools` table and are editable at Admin → Utensils.
 */
export function UtensilsMenu({ onNavigate }: { onNavigate?: () => void }) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    void get<{ tools: Tool[] }>("/tools")
      .then((r) => setTools(r.tools))
      .catch(() => undefined);
  }, []);

  // Close on navigation, Escape, or a click outside.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  // Nothing configured yet: don't render an empty menu.
  if (tools.length === 0) return null;

  return (
    <div className="utensils" ref={wrapper}>
      <button
        type="button"
        className={`nav-link utensils__trigger ${open ? "is-open" : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="tag">Utensils</span>
        <span className="utensils__chevron" aria-hidden>
          ▾
        </span>
      </button>

      {open && (
        <div className="utensils__panel" role="menu">
          <p className="utensils__heading">Tools</p>
          {tools.map((tool) => (
            <a
              key={tool.id}
              className="utensils__item"
              role="menuitem"
              href={tool.href ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
            >
              <span className="utensils__icon" aria-hidden>
                {tool.iconUrl ? (
                  <img src={tool.iconUrl} alt="" loading="lazy" />
                ) : (
                  (tool.icon ?? tool.label.slice(0, 1).toUpperCase())
                )}
              </span>
              <span className="utensils__text">
                <span className="utensils__label">{tool.label}</span>
                {tool.description ? (
                  <span className="utensils__desc">{tool.description}</span>
                ) : null}
              </span>
              <span className="utensils__external" aria-hidden>
                ↗
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
